/** Accessible toggle switch. Wraps React Aria Switch. */
import { Switch as AriaSwitch, type SwitchProps as AriaSwitchProps } from 'react-aria-components';
import type { ReactNode } from 'react';
import { cn } from '@/foundation/utils/cn';

interface SwitchProps extends Omit<AriaSwitchProps, 'children'> {
  children?: ReactNode;
  className?: string;
  description?: string;
}

function Switch({ children, className, description, ...props }: SwitchProps) {
  return (
    <AriaSwitch
      {...props}
      className={cn('group flex items-center gap-3 cursor-pointer', className)}
    >
      {(renderProps) => (
        <>
          <div
            className={cn(
              'w-9 h-5 rounded-full transition-colors duration-[var(--f-duration-fast)] flex items-center px-0.5',
              renderProps.isSelected
                ? 'bg-[color:var(--f-accent)]'
                : 'bg-[color:var(--f-border-strong)]',
              renderProps.isFocusVisible && 'ring-2 ring-offset-1 ring-[color:var(--f-accent)]',
              renderProps.isDisabled && 'opacity-40 cursor-not-allowed'
            )}
          >
            <div
              className={cn(
                'w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-[var(--f-duration-fast)]',
                renderProps.isSelected && 'translate-x-4'
              )}
            />
          </div>
          {(children || description) && (
            <div className="flex flex-col">
              {children && (
                <span className="text-[length:var(--f-text-sm)] text-[color:var(--f-fg)]">
                  {children}
                </span>
              )}
              {description && (
                <span className="text-[length:var(--f-text-xs)] text-[color:var(--f-fg-muted)]">
                  {description}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </AriaSwitch>
  );
}

export { Switch };
export type { SwitchProps };
